package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Les conversations avec l'assistant et leurs messages dans le moteur de synchronisation.
 *
 * Une conversation va et vient comme toute ligne ; `last_message_at` est écrit par le
 * serveur seul, et ne repart pas. Un message ne repart jamais : le serveur les écrit
 * tous, et refuserait ceux d'un appareil. Ils n'ont donc rien à envoyer, même après une
 * remise à zéro, et leur suppression suit celle de leur conversation, côté serveur.
 */
internal object AssistantSyncOps {

    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = when (entity) {
        EntityKind.AssistantConversation -> db.assistantConversationQueries.selectDirty()
        else -> db.assistantMessageQueries.selectNone()
    }

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.AssistantConversation -> db.assistantConversationQueries.selectDirty().executeAsList().map { conversation(it) }
        else -> emptyList()
    }

    fun selectAll(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.AssistantConversation -> db.assistantConversationQueries.selectAll().executeAsList()
            .filter { it.push_error == null }.map { conversation(it) }
        else -> emptyList()
    }

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        when (entity) {
            EntityKind.AssistantConversation -> db.assistantConversationQueries.markSynced(syncedAt, id, updatedAt)
            else -> db.assistantMessageQueries.markSynced(syncedAt, id, updatedAt)
        }
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? = when (entity) {
        EntityKind.AssistantConversation -> db.assistantConversationQueries.selectByIdAnyState(id).executeAsOneOrNull()
            ?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        else -> db.assistantMessageQueries.selectByIdAnyState(id).executeAsOneOrNull()
            ?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
    }

    fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.AssistantConversation -> db.assistantConversationQueries.insertFromRemote(
                row.id,
                p.str("skill_id") ?: "",
                p.str("title"),
                p.long("created_at") ?: row.updatedAt,
                p.long("last_message_at"),
                row.updatedAt,
            )
            else -> db.assistantMessageQueries.insertFromRemote(
                row.id,
                p.str("conversation_id") ?: "",
                p.str("role") ?: "assistant",
                p.str("content") ?: "",
                p.str("sources"),
                p.str("status") ?: "complete",
                p.str("error_code"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
                row.updatedAt,
            )
        }
    }

    fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.AssistantConversation -> db.assistantConversationQueries.updateFromRemote(
                p.str("skill_id") ?: "",
                p.str("title"),
                p.long("last_message_at"),
                row.updatedAt,
                row.id,
            )
            else -> db.assistantMessageQueries.updateFromRemote(
                p.str("conversation_id") ?: "",
                p.str("role") ?: "assistant",
                p.str("content") ?: "",
                p.str("sources"),
                p.str("status") ?: "complete",
                p.str("error_code"),
                row.updatedAt,
                row.updatedAt,
                row.id,
            )
        }
    }

    fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow, deletedAt: Long) {
        when (entity) {
            EntityKind.AssistantConversation -> db.assistantConversationQueries.softDelete(deletedAt, row.updatedAt, row.id)
            else -> db.assistantMessageQueries.softDelete(deletedAt, row.updatedAt, row.updatedAt, row.id)
        }
    }

    fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        when (entity) {
            EntityKind.AssistantConversation -> db.assistantConversationQueries.restore(row.updatedAt, row.id)
            else -> db.assistantMessageQueries.restore(row.updatedAt, row.updatedAt, row.id)
        }
    }

    /** Le serveur a refusé la conversation (limite de 200) : elle ne repart plus. */
    fun markRefused(db: TeddDatabase, id: String, reason: String) {
        db.assistantConversationQueries.markRefused(reason, id)
    }

    // `last_message_at` n'y est pas : le serveur l'ignorerait.
    private fun conversation(it: com.tedd.db.Assistant_conversation) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "skill_id" to it.skill_id,
        "title" to it.title,
        "created_at" to it.created_at,
    ))
}
